<svelte:options
  customElement={{
    tag: "oc-selection-tile-v1",
    extend: window.__components.extend({ formAssociated: true, inputBehavior: true }),
    props: {
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" },
      checked: { type: "Boolean" }, /*                                     */
      variant: { type: "String" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
      color: { type: "String" },
      fillParent: { type: "Boolean", attribute: "fill-parent" },
      singleSelection: { type: "Boolean", attribute: "single-selection" },
      strikeThrough: { type: "Boolean", attribute: "strike-through" },
      imgSrc: { type: "String", attribute: "img-src" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
    },
  }}
/>

<script lang="ts">
  import {
    implicitSubmit,
    refireNonComposableNativeEvent,
    singleSelection as useSingleSelection,
    stopLabelClickPropagation,
  } from "../../../common/actions";
  import type { Props } from "./SelectionTileV1.types.js";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";

  let {
    variant = "text",
    disabled = false,
    checked = false,
    value = "on",
    name = undefined,
    fillParent = false,
    color = undefined,
    imgSrc = undefined,
    singleSelection = false,
    strikeThrough = false,
    ocAriaLabel = undefined,
    internals,
  }: Props & {
    internals: ElementInternals;
  } = $props();

  const Host = $host();

  export function resetForm(): void {
    /*                            */
    checked = Host.hasAttribute("checked");
  }

  $effect(() => {
    internals.setFormValue(checked ? value : null);
  });

  const onChange = (event: Event): void => {
    const input = event.target as HTMLInputElement;
    if (!event.defaultPrevented) checked = input.checked;
  };

  let clientWidth = $state(0);
  let clientHeight = $state(0);
  let strikeThroughSize = $derived(`${Math.min(clientWidth, clientHeight)}px`);
</script>

<label
  class="selection-tile {`selection-tile--variant-${variant}`} "
  class:selection-tile--fill-parent={fillParent}
  class:selection-tile--checked={checked}
  class:selection-tile--disabled={disabled}
  class:selection-tile--variant-container--fill-parent={variant === "container" && fillParent}
  style:--color={color}
  style:--imgSrc={imgSrc ? `url(${imgSrc})` : undefined}
  use:stopLabelClickPropagation
  bind:clientWidth
  bind:clientHeight
  style:--strike-through-size={strikeThroughSize}
>
  {#if singleSelection}
    <input
      type="radio"
      class="selection-tile__input"
      {checked}
      {name}
      {disabled}
      use:useSingleSelection={{ Host, name, checked }}
      use:refireNonComposableNativeEvent={Host}
      use:implicitSubmit={internals}
      onchange={onChange}
      aria-label={ocAriaLabel}
    />
  {:else}
    <input
      type="checkbox"
      class="selection-tile__input"
      bind:checked
      {name}
      {disabled}
      use:refireNonComposableNativeEvent={Host}
      use:implicitSubmit={internals}
      aria-label={ocAriaLabel}
    />
  {/if}
  {#if variant === "text" || variant === "container"}
    <slot />
  {/if}
  {#if strikeThrough}
    <span class="selection-tile__strikethrough"> </span>
  {/if}
</label>

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    :host {
      /*        */
      --color: initial;
      --imgSrc: initial;
    }

    .selection-tile {
      position: relative;
      display: block;
      text-align: center;
      cursor: pointer;
      color: tokens.$oc-component-selection-tile-text-color;

      outline: #{tokens.$oc-component-selection-tile-border-color} solid
        #{tokens.$oc-component-selection-tile-border-width};

      width: fit-content;
      &--fill-parent {
        width: 100%;
      }
    }

    .selection-tile--variant-container,
    .selection-tile--variant-text {
      background: tokens.$oc-component-selection-tile-background-color;
      border-radius: tokens.$oc-component-selection-tile-roundrect-border-radius;
    }

    .selection-tile--variant-text {
      padding: 12px 16px;
      font: tokens.$oc-component-selection-tile-text-font;
    }

    .selection-tile--variant-color,
    .selection-tile--variant-image {
      border-radius: 50%;
      height: tokens.$oc-component-selection-tile-round-size;
      width: tokens.$oc-component-selection-tile-round-size;
    }

    .selection-tile--variant-color {
      background: var(--color); /*                                                                */
      height: tokens.$oc-component-selection-tile-round-size;
      width: tokens.$oc-component-selection-tile-round-size;
    }

    .selection-tile--variant-image {
      background-image: var(--imgSrc);
      background-position: center;
      background-size: 100%;

      height: tokens.$oc-component-selection-tile-round-size;
      width: tokens.$oc-component-selection-tile-round-size;
    }

    .selection-tile__input {
      all: unset;
      position: absolute;
      width: 100%;
      height: 100%;

      --oc-floating-focus-v1-full-bleed: "";

      /*                             */
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      min-width: tokens.$oc-component-selection-tile-round-hitbox-size;
      min-height: tokens.$oc-component-selection-tile-round-hitbox-size;
    }

    /*            */
    .selection-tile--checked {
      outline-color: #{tokens.$oc-component-selection-tile-border-color-selected};
      outline-width: #{tokens.$oc-component-selection-tile-border-width-selected};
    }

    /*          */
    @media (hover: hover) {
      .selection-tile:hover,
      .selection-tile--checked:hover {
        &.selection-tile--variant-container,
        &.selection-tile--variant-text {
          --internal-border-radius: #{tokens.$oc-component-selection-tile-roundrect-border-radius};
        }

        &.selection-tile--variant-color,
        &.selection-tile--variant-image {
          --internal-border-radius: 50%;
        }

        &:after {
          content: "";
          position: absolute;
          inset: calc(-1 * var(--internal-border-offset));
          border-radius: var(--internal-border-radius);
          background-color: tokens.$oc-component-interactive-overlay-background-color-hover;
          pointer-events: none;
        }
      }

      .selection-tile:hover {
        --internal-border-offset: 1px;
        outline-offset: 1px;
      }
    }

    /*           */
    .selection-tile:active,
    .selection-tile--checked:active {
      &.selection-tile--variant-container,
      &.selection-tile--variant-text {
        --internal-border-radius: #{tokens.$oc-component-selection-tile-roundrect-border-radius};
      }

      &.selection-tile--variant-color,
      &.selection-tile--variant-image {
        --internal-border-radius: 50%;
      }

      &:after {
        content: "";
        position: absolute;
        inset: calc(-1 * var(--internal-border-offset));
        border-radius: var(--internal-border-radius);
        background-color: tokens.$oc-component-interactive-overlay-background-color-active !important;
        pointer-events: none;
      }
    }

    .selection-tile:active {
      --internal-border-offset: 1px;
      outline-offset: 1px;
    }

    .selection-tile.selection-tile--disabled,
    .selection-tile--checked.selection-tile--disabled {
      cursor: not-allowed;
      outline-color: #{tokens.$oc-component-selection-tile-disabled-border-color};
      outline-width: #{tokens.$oc-component-selection-tile-disabled-border-width};

      &:before {
        content: "";
        position: absolute;
        border-radius: inherit;
        inset: 0;
        background: #{tokens.$oc-component-selection-tile-disabled-overlay};
      }
    }

    .selection-tile.selection-tile--disabled {
      --internal-border-offset: unset;
      outline-offset: unset;
    }

    /*            */
    .selection-tile__strikethrough {
      padding: tokens.$oc-semantic-spacing-50;
      position: absolute;

      height: var(--strike-through-size);
      width: var(--strike-through-size);

      /*                       */
      inset: 0;
      margin: auto;

      &:after {
        content: "";
        position: absolute;
        border-radius: tokens.$oc-semantic-border-radius-200;
        right: tokens.$oc-semantic-spacing-50;
        top: tokens.$oc-semantic-spacing-50;

        height: calc(1.41 * (100% - 2 * #{tokens.$oc-semantic-spacing-50}));
        background: tokens.$oc-component-selection-tile-strike-through-color;
        width: tokens.$oc-component-selection-tile-strike-through-width;
        outline: tokens.$oc-component-selection-tile-strike-through-outline-color solid
          tokens.$oc-component-selection-tile-strike-through-outline-width;

        transform-origin: top left;
        transform: rotate(45deg);
      }
    }
  }
</style>
